import React, { useState, MouseEvent } from "react";
import { DownloadIndicator } from "./DownloadIndicator";
import OutsideClickHandler from "react-outside-click-handler";

interface DownloadButtonProps {
  downloadState: string;
  onAbort: () => void;
  progress: number | undefined;
  onDelete: () => void;
  onDownload: () => void;
  offline: boolean;
  size?: number;
  disabled?: boolean;
}

function getButtonProps({
  downloadState,
  active,
  onDelete,
  onDownload,
  onAbort,
  offline,
  setActive
}: DownloadButtonProps & {
  active: boolean;
  setActive: (state: boolean) => void;
}) {
  // default
  let props = {
    indicatorState: "download",
    disabled: offline,
    onClick: () => {
      onDownload();
      setActive(false);
    }
  };

  switch (downloadState) {
    case "FAILED":
      if (active) {
        props.indicatorState = "retry";
        props.onClick = () => {
          onDownload();
          setActive(false);
        };
        break;
      }

      props.indicatorState = "failed";
      props.onClick = () => setActive(true);
      break;
    case "INITIALIZING":
      props.indicatorState = "initializing";
      props.onClick = () => {
        onAbort();
        setActive(false);
      };
      break;
    case "ABORTING":
      props.indicatorState = "aborting";
      props.disabled = true;
      props.onClick = () => {};
      break;
    case "DOWNLOADED":
      if (active) {
        props.indicatorState = "delete";
        props.onClick = () => {
          onDelete();
          setActive(false);
        };
        props.disabled = false; // ensure action is available offline
        break;
      }

      props.indicatorState = "downloaded";
      props.onClick = () => setActive(true);
      props.disabled = false; // ensure action is available offline
      break;
    case "DOWNLOADING":
      if (active) {
        props.indicatorState = "cancel";
        props.onClick = () => {
          onAbort();
          setActive(false);
        };
        break;
      }

      props.indicatorState = "downloading";
      props.onClick = () => setActive(true);
      break;
    case "PARTIAL_DOWNLOAD":
    default:
      break;
  }

  return props;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  downloadState,
  onAbort,
  progress,
  onDelete,
  onDownload,
  size = 24,
  offline,
  disabled: disabledOverride,
  ...props
}) => {
  const [active, setActive] = useState(false);

  const { indicatorState, onClick, disabled } = getButtonProps({
    downloadState,
    onAbort,
    onDownload,
    onDelete,
    active,
    setActive,
    progress,
    offline
  });

  const preventPropegation = (handler: any) => (
    event: MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    handler();
  };

  return (
    <OutsideClickHandler onOutsideClick={() => active && setActive(false)}>
      <DownloadIndicator
        key={indicatorState}
        indicatorState={indicatorState}
        progress={progress}
        onClick={preventPropegation(onClick)}
        size={size}
        active={active}
        offline={offline}
        disabled={disabled || disabledOverride}
        {...props}
      />
    </OutsideClickHandler>
  );
};
